import { ReactElement, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { toNumber } from 'lodash-es'
import CloseIcon from '@mui/icons-material/Close'
import { Box, Checkbox, FormControlLabel, IconButton, Stack, Typography } from '@mui/material'
import type {
  ShearAnchorPosition,
  ShearConnectionDTO,
  ShearConnectionPrioritizationDTO,
} from '@api/fastAPI.schemas'
import {
  useGetAllProjectShearConnections,
  useGetProjectShearConnectionPrioritization,
  useGetShearAnchorStoreySelection,
  useSetShearAnchorStoreySelection,
} from '@api/project-shear-connections/project-shear-connections'
import { PanelHeader, Section } from '@ui2/structure'
import { ErrorFieldBase } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import { useEditorViewStore, roofStoreyKey } from '@editorStores'
import { useModelStoreys, useResultsInvalidation } from '@editorHooks'
import {
  formatShearDistance,
  formatShearResistance,
  isShearAnchorActive,
  listsWithoutActiveAnchor,
  SHEAR_ANCHOR_POSITION_LABEL,
  SHEAR_PLACEMENT_LABEL,
  SHEAR_PLACEMENTS_AND_POSITIONS,
  shearCategoryForStorey,
  shearListLabel,
  shearStoreyLabel,
  type ShearPlacement,
} from 'src/components/manager/shearConnections'
import { buildErrorMessage } from 'src/constants/errors'
import { resolveStoreySelectionToggle } from './utils'

/**
 * "Wählbare Schubanker je Geschoss" panel: per storey (top-down), wall placement and anchor
 * position, which anchors of the prioritization the check may use there. Stored as exclusions, so
 * everything is checked until the user unchecks it — see the backend's
 * `ProjectShearAnchorStoreySelection`.
 */
const Results2ShearAnchorSelectionPane = (): ReactElement => {
  const { projectId } = useParams()
  const projectGuid = projectId as string
  const { enqueueSnackbar } = useSnackbar()
  const invalidateResults = useResultsInvalidation()
  const setOpen = useEditorViewStore(state => state.setResultsShearAnchorSelectionOpen)

  const allStoreys = useModelStoreys()
  // Walls only live on numeric storeys; listed top-down like the storey selects.
  const storeys = useMemo(
    () =>
      allStoreys
        .filter(storey => storey !== roofStoreyKey)
        .map(toNumber)
        .sort((a, b) => b - a),
    [allStoreys],
  )
  const topStorey = storeys.length > 0 ? Math.max(...storeys) : 0

  const { data: connections } = useGetAllProjectShearConnections<ShearConnectionDTO[], AxiosError>(
    projectGuid,
    {
      query: {
        onError: error => {
          enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Schubverbinder'), {
            variant: 'error',
          })
        },
      },
    },
  )
  const { data: prioritization } = useGetProjectShearConnectionPrioritization<
    ShearConnectionPrioritizationDTO,
    AxiosError
  >(projectGuid, {
    query: {
      onError: error => {
        enqueueSnackbar(
          buildErrorMessage(error, 'Fehler beim Laden der Schubverbinderpriorisierung'),
          { variant: 'error' },
        )
      },
    },
  })
  const { data: selection } = useGetShearAnchorStoreySelection(projectGuid, {
    query: {
      // The backend normalises the selection on manager edits, which happen on
      // another page while this query is unmounted; with the app's
      // refetchOnMount:false default the invalidation from
      // useResultsInvalidation would never land (cf. useShearAnchorChecks).
      refetchOnMount: true,
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Schubankerauswahl'), {
          variant: 'error',
        })
      },
    },
  })

  const { mutate: saveSelection, isLoading: isSaving } = useSetShearAnchorStoreySelection<
    AxiosError,
    unknown
  >({
    mutation: {
      meta: {
        title: 'Schubankerauswahl speichern',
        description: 'Speichert die je Geschoss relevanten Schubanker',
      },
      // `invalidateResults` already invalidates this selection query -- the
      // backend normalises the exclusions on every save -- so invalidating it
      // separately here would only cost a second refetch of the same endpoint.
      onSuccess: async () => {
        await invalidateResults(projectGuid)
      },
      onError: error => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern der Schubankerauswahl'), {
          variant: 'error',
        })
      },
    },
  })

  const connectionsByGuid = useMemo(
    () => Object.fromEntries((connections ?? []).map(connection => [connection.guid, connection])),
    [connections],
  )

  const emptyListLabels = useMemo(
    () =>
      selection && prioritization
        ? listsWithoutActiveAnchor(selection, prioritization, storeys)
        : [],
    [selection, prioritization, storeys],
  )

  const handleToggle = (
    storey: number,
    placement: ShearPlacement,
    position: ShearAnchorPosition,
    anchorGuid: string,
    nextActive: boolean,
  ) => {
    if (!selection || !prioritization) return
    const change = resolveStoreySelectionToggle({
      selection,
      storeyKey: String(storey),
      placement,
      position,
      anchorGuid,
      nextActive,
      orderedGuids:
        prioritization[shearCategoryForStorey(storey, topStorey, placement, position)] ?? [],
      listLabel: shearListLabel(storey, topStorey, placement, position),
    })
    if (change.kind === 'refused') {
      enqueueSnackbar(change.message, { variant: 'error' })
      return
    }
    saveSelection({ projectGuid, data: change.selection })
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <PanelHeader
        title="Wählbare Schubanker je Geschoss einstellen"
        description="Wähle hier aus, welche Schubanker für welches Geschoss verwendet werden sollen."
        action={
          <IconButton
            size="small"
            onClick={() => setOpen(false)}
            data-cy="shear-anchor-selection-close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2 }}>
        <Stack spacing={2}>
          {emptyListLabels.length > 0 && (
            <ErrorFieldBase
              severity="warning"
              message={`Keine aktiven Schubanker für: ${emptyListLabels.join(
                ', ',
              )} — dort wird der erste Schubanker der Priorisierung verwendet`}
              data-cy="shear-anchor-selection-warning"
            />
          )}

          {storeys.map(storey => (
            <Section key={storey} title={shearStoreyLabel(storey, topStorey)} ui2>
              <Stack spacing={1} sx={{ pb: 1 }}>
                {SHEAR_PLACEMENTS_AND_POSITIONS.map(({ placement, position }) => (
                  <Box key={`${placement}-${position}`}>
                    <Typography variant="caption" color="text.secondary">
                      {`${SHEAR_PLACEMENT_LABEL[placement]} ${SHEAR_ANCHOR_POSITION_LABEL[position]}`}
                    </Typography>
                    <Stack>
                      {(
                        prioritization?.[
                          shearCategoryForStorey(storey, topStorey, placement, position)
                        ] ?? []
                      ).map(anchorGuid => {
                        const connection = connectionsByGuid[anchorGuid]
                        if (!connection) return null
                        return (
                          <FormControlLabel
                            key={anchorGuid}
                            control={
                              <Checkbox
                                size="small"
                                checked={
                                  !!selection &&
                                  isShearAnchorActive(
                                    selection,
                                    String(storey),
                                    placement,
                                    position,
                                    anchorGuid,
                                  )
                                }
                                disabled={!selection || isSaving}
                                onChange={(_, checked) =>
                                  handleToggle(storey, placement, position, anchorGuid, checked)
                                }
                                data-cy={`shear-anchor-selection-${storey}-${placement}-${position}-${anchorGuid}`}
                              />
                            }
                            label={
                              <Typography variant="body2">
                                {connection.name}
                                <Typography
                                  component="span"
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ ml: 1 }}
                                >
                                  {`${formatShearResistance(
                                    connection.r_d_wind,
                                  )} / ${formatShearResistance(
                                    connection.r_d_earthquake,
                                  )} kN je ${formatShearDistance(connection.distance)} m`}
                                </Typography>
                              </Typography>
                            }
                          />
                        )
                      })}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Section>
          ))}
        </Stack>
      </Box>
    </Box>
  )
}

export default Results2ShearAnchorSelectionPane
